import type { Guide, GuideRecordHint } from '../types';

/**
 * S-24.10-11 — weryfikacja przewoźnika i akceptacja przy niepełnej weryfikacji (paczka p. Mariusza 21.10,
 * BR-TRD-13; scenariusz e2e 28). Rekordy z `e2e/setup/seed-e2e.ts` (`g3Trade2410`): przewoźnik
 * „DEMO Przewoźnik A” z niepełną weryfikacją (OCP potwierdzone, licencji brak, VAT nie wpisano) i zlecenie
 * DEMO/2026/10/G31 w Trade z jego otwartą ofertą. Nagrywarka tylko otwiera okna, niczego nie zapisuje.
 */
const CARRIER: GuideRecordHint = {
  description: 'dowolny przewoźnik z kartoteki',
  listPath: '/carriers',
};

const TRADE_ORDER: GuideRecordHint = {
  description: 'zlecenie w Trade z otwartą ofertą przewoźnika',
  listPath: '/user-offers',
  listFilter: { forklift: 'hide', dzial: 'TRADE', carrierOffers: true },
};

const DEMO_CARRIER = { kind: 'carrier', name: 'DEMO Przewoźnik A (dane testowe)' } as const;
const DEMO_ORDER = { kind: 'order', number: 'DEMO/2026/10/G31' } as const;

export const guide: Guide = {
  flagKey: 'carrierVerification',
  title: 'Weryfikacja przewoźnika i akceptacja mimo niepełnej weryfikacji',
  whatItDoes:
    'W kartotece przewoźnika jest sekcja „Weryfikacja”: ubezpieczenie OCP z terminem ważności, licencja albo zezwolenie i VAT czynny z dniem sprawdzenia. Trader wpisuje je po sprawdzeniu dokumentów — przy każdej pozycji widać stan, kto i kiedy ją wpisał, a status zbiorczy mówi, czy weryfikacja jest pełna, niepełna czy nieustalona. Przewoźnika z niepełną albo nieustaloną weryfikacją można zaakceptować, ale tylko z uzasadnieniem — akceptacja zapisuje się w historii zlecenia jako wyjątek.',
  audience: ['Trade'],
  presentationItems: ['S-24.10-11'],
  steps: [
    {
      route: '/carriers/:carrierId/show',
      record: CARRIER,
      target: 'carrier-verification',
      title: 'Sekcja „Weryfikacja” na karcie przewoźnika',
      description:
        'Trzy pozycje: ubezpieczenie OCP, licencja / zezwolenie i VAT czynny — każda ze stanem (potwierdzone, brak, po terminie, nie wpisano) i informacją, kto i kiedy ją wpisał. Status zbiorczy „pełna” jest tylko wtedy, gdy wszystkie trzy są potwierdzone; „nieustalona” nie oznacza zgody.',
      action: 'Otwórz przewoźnika z listy „Przewoźnicy” i znajdź sekcję „Weryfikacja”.',
      recorder: { account: 'trade', demo: DEMO_CARRIER },
    },
    {
      route: '/carriers/:carrierId/show',
      record: CARRIER,
      target: 'carrier-verification-dialog',
      title: 'Wpis pozycji',
      description:
        'Przy ubezpieczeniu OCP podaje się termin ważności, przy licencji — opcjonalnie numer i termin, przy VAT — czy jest czynny i kiedy sprawdzono. Każda zmiana trafia do dziennika działań.',
      action: 'Kliknij „Wpisz” albo „Zmień” przy pozycji, uzupełnij pola i zapisz.',
      recorder: {
        account: 'trade',
        demo: DEMO_CARRIER,
        actions: [{ kind: 'click', target: 'carrier-verification-edit-license' }],
      },
    },
    {
      route: '/user-offers/:orderId/show',
      record: TRADE_ORDER,
      target: 'accept-verification',
      title: 'Akceptacja mimo niepełnej weryfikacji',
      description:
        'W oknie akceptacji widać status weryfikacji przewoźnika. Przy niepełnej albo nieustalonej ostrzeżenie wymienia braki, a akceptacja wymaga uzasadnienia — nic nie jest blokowane, a historia zlecenia dostaje wpis „akceptacja mimo niepełnej weryfikacji”.',
      action: 'Przy ofercie kliknij „Akceptuj przewoźnika i stawkę” i przeczytaj ostrzeżenie.',
      recorder: {
        account: 'trade',
        demo: DEMO_ORDER,
        actions: [{ kind: 'click', target: 'trd-negocjacja-akceptuj-przewoznika' }],
      },
    },
  ],
};
